import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Wallet, Plus, Camera, Trash2, Pencil, X, Search, Loader2, PiggyBank,
  Paperclip, AlertTriangle, ChevronDown, ChevronUp, ScanLine, FileDown, Users, Sparkles, Send,
  MessageCircle, MapPin, CopyPlus, Share2, Copy, Code2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { auth } from "../../lib/firebase";
import { ReceiptGallery, SpendingInsights } from "./ExpenseExtras";

/* My Trip Expenses — per-trip ledger with receipt OCR scan, multi-currency,
   budgets + alerts, category/daily summaries and search. */

export const EXPENSE_CATEGORIES = [
  { id: "transportation", label: "Transportation", cls: "bg-sky-100 text-sky-800 border-sky-300", subs: ["Airfare", "Train", "Bus", "Subway/Metro", "Taxi", "Rideshare", "Rental Car", "Fuel", "Parking", "Tolls", "Ferry", "Other Transportation"] },
  { id: "accommodation", label: "Accommodation", cls: "bg-indigo-100 text-indigo-800 border-indigo-300", subs: ["Hotel", "Motel", "Vacation Rental", "Hostel", "Resort", "Lodging Tax", "Resort Fee", "Other Accommodation"] },
  { id: "food", label: "Food & Dining", cls: "bg-amber-100 text-amber-800 border-amber-300", subs: ["Restaurant", "Breakfast", "Lunch", "Dinner", "Café/Coffee", "Snacks", "Groceries", "Room Service", "Bar/Beverage", "Food Delivery"] },
  { id: "activities", label: "Activities & Entertainment", cls: "bg-violet-100 text-violet-800 border-violet-300", subs: ["Attractions", "Entry Fees", "Museums", "Tours", "Events", "Theater", "Sports", "Recreation", "Excursions"] },
  { id: "shopping", label: "Shopping", cls: "bg-pink-100 text-pink-800 border-pink-300", subs: ["Clothing", "Gifts", "Souvenirs", "Electronics", "Personal Items", "Duty Free", "Other Shopping"] },
  { id: "health", label: "Health", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", subs: ["Pharmacy", "Medicine", "Doctor", "Medical Treatment", "Medical Supplies"] },
  { id: "communication", label: "Communication", cls: "bg-cyan-100 text-cyan-800 border-cyan-300", subs: ["Mobile", "Roaming", "SIM/eSIM", "Internet", "Wi-Fi"] },
  { id: "travel_services", label: "Travel Services", cls: "bg-teal-100 text-teal-800 border-teal-300", subs: ["Visa", "Passport", "Travel Insurance", "Baggage Fees", "Seat Fees", "Lounge", "Travel Agency", "Other Travel Services"] },
  { id: "financial", label: "Financial", cls: "bg-orange-100 text-orange-800 border-orange-300", subs: ["ATM Fee", "Foreign Transaction Fee", "Currency Exchange Fee", "Bank Fee"] },
  { id: "other", label: "Other", cls: "bg-zinc-100 text-zinc-700 border-zinc-300", subs: ["Tips", "Taxes", "Fines", "Miscellaneous", "Other"] },
];
const catOf = (id) => EXPENSE_CATEGORIES.find((c) => c.id === id) || EXPENSE_CATEGORIES[EXPENSE_CATEGORIES.length - 1];
const PAYMENTS = ["Cash", "Credit Card", "Debit Card", "Travel Card", "Mobile Wallet", "Bank Transfer", "Gift Card", "Coupon / Voucher", "Other"];
const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "CAD", "AUD", "CHF", "CNY", "INR", "MXN", "BRL", "KRW", "SGD", "AED", "THB", "TRY", "NZD", "SEK", "NOK", "DKK"];
const REIMB = [["n/a", "N/A"], ["not_submitted", "Not submitted"], ["submitted", "Submitted"], ["approved", "Approved"], ["reimbursed", "Reimbursed"]];

const money = (n, ccy = "") => n == null ? "—" : `${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${ccy ? ` ${ccy}` : ""}`;
const input = "border border-zinc-300 px-2.5 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-primary w-full";
const lbl = "block text-xs font-700 text-zinc-600 mb-1";

const emptyForm = (home) => ({
  date: new Date().toISOString().slice(0, 10), time: "", merchant: "", description: "",
  category: "food", subcategory: "", amount: "", currency: home || "USD",
  payment_method: "Credit Card", card_last4: "", paid_by: "Me", expense_for: "Me",
  location: "", address: "", phone: "", website: "", email: "",
  tax: "", tip: "", discount: "", exchange_rate: "", rate_source: "",
  home_amount: "", home_currency: home || "USD", business: false, reimbursable: false,
  reimbursement_status: "n/a", notes: "", receipt_id: null, receipt_url: null, ocr_data: null, split: [],
});

export default function TripExpenses({ tripId, tripTitle, readOnly, isOwner, people = [] }) {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);      // {form, editId, fromScan, ocrError}
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("");
  const [fPay, setFPay] = useState("");
  const [downloading, setDownloading] = useState(false);
  const scanRef = useRef(null);

  const download = async (fmt) => {
    setDownloading(true);
    try {
      const r = await api.get(`/expenses/trip/${tripId}/report.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trip-expenses.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the report"); }
    finally { setDownloading(false); }
  };

  const load = useCallback(() => api.get(`/expenses/trip/${tripId}`).then((r) => setData(r.data)).catch(() => {}), [tripId]);
  useEffect(() => { load(); }, [load]);

  const home = data?.summary?.home_currency || "USD";
  const items = useMemo(() => data?.items || [], [data]);
  const sum = data?.summary;
  const catEntries = useMemo(() => Object.entries(sum?.by_category || {}).sort((a, b) => b[1] - a[1]), [sum]);

  const filtered = useMemo(() => {
    let list = items;
    if (fCat) list = list.filter((e) => e.category === fCat);
    if (fPay) list = list.filter((e) => e.payment_method === fPay);
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      list = list.filter((e) => [e.merchant, e.description, e.location, e.subcategory, e.notes, catOf(e.category).label, String(e.amount)]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(t)));
    }
    return list;
  }, [items, q, fCat, fPay]);

  const scan = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/expenses/trip/${tripId}/scan`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { receipt, draft, ocr_error } = r.data;
      setModal({
        editId: null, fromScan: true, ocrError: ocr_error,
        form: { ...emptyForm(home), ...Object.fromEntries(Object.entries(draft || {}).filter(([, v]) => v != null && v !== "")),
                home_currency: home, receipt_id: receipt.id, receipt_url: receipt.url },
      });
      if (!ocr_error) toast.success("Receipt scanned — review the details before saving");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not scan the receipt"); }
    finally { setScanning(false); if (scanRef.current) scanRef.current.value = ""; }
  };

  const save = async (form, editId) => {
    const payload = { ...form };
    ["amount", "tax", "tip", "discount", "exchange_rate", "home_amount"].forEach((k) => { payload[k] = payload[k] === "" ? null : payload[k]; });
    payload.split = (payload.split || []).filter((s) => s.name?.trim() && s.amount !== "" && s.amount != null);
    try {
      if (editId) await api.put(`/expenses/${editId}`, payload);
      else await api.post(`/expenses/trip/${tripId}`, payload);
      setModal(null);
      load();
      toast.success(editId ? "Expense updated" : "Expense saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check the amount and try again");
    }
  };

  const duplicate = async (e) => {
    const next = e.date ? new Date(new Date(e.date).getTime() + 86400000).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
    const { id, created_at, receipt_id, receipt_url, ocr_data, ...rest } = e;
    try {
      await api.post(`/expenses/trip/${tripId}`, { ...rest, date: next });
      load();
      toast.success(`Duplicated to ${next}`);
    } catch { toast.error("Could not duplicate"); }
  };

  const remove = async (eid) => {
    try { await api.delete(`/expenses/${eid}`); load(); }
    catch { toast.error("Could not delete"); }
  };

  const token = localStorage.getItem("token");

  return (
    <section id="expenses" className="mb-10 scroll-mt-24" data-testid="trip-expenses">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="font-heading font-700 text-xl flex items-center gap-2">
          <Wallet className="w-5 h-5 text-[#b45309]" /> My Trip Expenses
        </h2>
        {sum && sum.count > 0 && (
          <span className="font-mono text-sm text-zinc-700" data-testid="expenses-total">
            <b>{money(sum.total, home)}</b> · {sum.count} expense{sum.count === 1 ? "" : "s"}
          </span>
        )}
        {!readOnly && (
          <span className="ml-auto flex flex-wrap gap-2">
            <button onClick={() => setModal({ editId: null, form: emptyForm(home) })} data-testid="expense-add-btn"
              className="inline-flex items-center gap-1.5 border border-[#b45309] text-[#b45309] px-3 py-2 text-sm font-700 hover:bg-amber-50">
              <Plus className="w-4 h-4" /> Add Expense
            </button>
            <button onClick={() => scanRef.current?.click()} disabled={scanning} data-testid="expense-scan-btn"
              className="inline-flex items-center gap-1.5 bg-[#b45309] text-white px-3 py-2 text-sm font-700 hover:bg-amber-700 disabled:opacity-60">
              {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {scanning ? "Reading receipt…" : "Scan Receipt"}
            </button>
            <input ref={scanRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
              className="hidden" data-testid="expense-scan-input" onChange={(e) => scan(e.target.files?.[0])} />
            <button onClick={() => setBudgetOpen(true)} data-testid="expense-budget-btn"
              className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-2 text-sm font-700 hover:border-[#b45309] hover:text-[#b45309]">
              <PiggyBank className="w-4 h-4" /> Budget
            </button>
            {items.length > 0 && (
              <>
                <button onClick={() => download("pdf")} disabled={downloading} data-testid="expense-report-pdf"
                  title="Download PDF expense report"
                  className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-2 text-sm font-700 hover:border-[#b45309] hover:text-[#b45309] disabled:opacity-50">
                  <FileDown className="w-4 h-4" /> PDF
                </button>
                <button onClick={() => download("csv")} disabled={downloading} data-testid="expense-report-csv"
                  title="Download CSV expense report"
                  className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-2 text-sm font-700 hover:border-[#b45309] hover:text-[#b45309] disabled:opacity-50">
                  <FileDown className="w-4 h-4" /> CSV
                </button>
              </>
            )}
            {isOwner && <ShareSummary tripId={tripId} tripTitle={tripTitle} />}
          </span>
        )}
      </div>

      {/* Alerts */}
      {(sum?.alerts || []).map((a, ai) => (
        <div key={`${ai}-${a.text}`} data-testid={`expense-alert-${a.level}`}
          className={`flex items-center gap-2 text-sm px-3 py-2 mb-2 border ${a.level === "red" ? "bg-rose-50 border-rose-300 text-rose-800" : "bg-amber-50 border-amber-300 text-amber-800"}`}>
          <AlertTriangle className="w-4 h-4 shrink-0" /> {a.text}
        </div>
      ))}

      {/* Summary dashboard */}
      {sum && sum.count > 0 && (
        <div className="border border-zinc-300 bg-white mb-4">
          <button onClick={() => setShowSummary(!showSummary)} data-testid="expense-summary-toggle"
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-700 hover:bg-zinc-50">
            <span>Summary — by category &amp; by day</span>
            {showSummary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showSummary && (
            <div className="p-4 pt-1">
              {Object.keys(sum.by_currency || {}).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="expense-currency-chips">
                  <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">By currency</span>
                  {Object.entries(sum.by_currency).map(([ccy, c]) => (
                    <span key={ccy} data-testid={`expense-ccy-${ccy.toLowerCase()}`}
                      className="inline-flex items-baseline gap-1.5 border border-zinc-300 bg-zinc-50 px-2.5 py-1 text-xs">
                      <span className="font-900 text-zinc-900">{ccy}</span>
                      <span className="font-mono font-700">{money(c.total)}</span>
                      {c.cash > 0 && <span className="font-mono text-[10px] text-emerald-700">cash {money(c.cash)}</span>}
                    </span>
                  ))}
                  <span className="text-[10px] text-muted-foreground">— totals in each original currency, handy for planning cash needs</span>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6" data-testid="expense-summary">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">By category</div>
                {catEntries.map(([cat, amt]) => {
                  const c = catOf(cat);
                  const cb = data?.budget?.category_budgets?.[cat];
                  const pct = sum.total ? Math.round(amt / sum.total * 100) : 0;
                  return (
                    <div key={cat} className="flex items-center gap-2 py-1 text-sm" data-testid={`expense-cat-row-${cat}`}>
                      <span className={`text-xs font-700 border px-1.5 py-0.5 ${c.cls}`}>{c.label}</span>
                      <span className="flex-1 h-1.5 bg-zinc-100"><span className="block h-1.5 bg-[#b45309]/60" style={{ width: `${pct}%` }} /></span>
                      <span className="font-mono">{money(amt, home)}</span>
                      {cb && <span className={`text-xs font-mono ${amt > cb ? "text-rose-600" : "text-muted-foreground"}`}>/ {money(cb)}</span>}
                    </div>
                  );
                })}
                {data?.budget?.total_budget && (
                  <div className="flex items-center justify-between border-t border-zinc-200 mt-2 pt-2 text-sm font-700" data-testid="expense-budget-line">
                    <span>Budget {money(data.budget.total_budget, home)}</span>
                    <span className={sum.total > data.budget.total_budget ? "text-rose-600" : "text-emerald-700"}>
                      {sum.total > data.budget.total_budget ? "Over by" : "Remaining"} {money(Math.abs(data.budget.total_budget - sum.total), home)}
                    </span>
                  </div>
                )}
                {sum.unconverted > 0 && <p className="text-xs text-muted-foreground mt-2">{sum.unconverted} expense(s) in foreign currency without a conversion — add an exchange rate for accurate totals.</p>}
                {sum.split?.active && (
                  <div className="border border-zinc-200 bg-zinc-50 p-3 mt-3 text-sm space-y-1" data-testid="expense-split-panel">
                    <div className="flex items-center gap-1.5 font-700 text-xs uppercase tracking-widest text-muted-foreground"><Users className="w-3.5 h-3.5" /> Split with co-travelers</div>
                    <div className="flex justify-between"><span>You paid</span><span className="font-mono font-700">{money(sum.split.you_paid, home)}</span></div>
                    <div className="flex justify-between"><span>Your share</span><span className="font-mono">{money(sum.split.your_share, home)}</span></div>
                    {sum.split.others_owe_you > 0 && <div className="flex justify-between text-emerald-700 font-700"><span>Others owe you</span><span className="font-mono" data-testid="split-owed-to-me">{money(sum.split.others_owe_you, home)}</span></div>}
                    {sum.split.you_owe_others > 0 && <div className="flex justify-between text-rose-700 font-700"><span>You owe others</span><span className="font-mono">{money(sum.split.you_owe_others, home)}</span></div>}
                    {Object.entries(sum.split.per_person || {}).map(([name, v]) => (
                      <div key={name} className="flex justify-between text-xs text-muted-foreground">
                        <span>{name}</span><span className="font-mono">{v > 0 ? `owes you ${money(v, home)}` : `you owe ${money(-v, home)}`}</span>
                      </div>
                    ))}
                    <button onClick={() => setSettleOpen(true)} data-testid="expense-settle-btn"
                      className="w-full mt-2 inline-flex items-center justify-center gap-1.5 border border-[#b45309] text-[#b45309] px-3 py-1.5 text-xs font-700 hover:bg-amber-50">
                      <Users className="w-3.5 h-3.5" /> Settle Up
                    </button>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Daily spending</div>
                {Object.entries(sum.by_day).slice(-14).map(([day, amt]) => (
                  <div key={day} className="flex items-center justify-between py-1 text-sm border-b border-zinc-100" data-testid={`expense-day-${day}`}>
                    <span className="font-mono text-xs">{day}</span><span className="font-mono">{money(amt, home)}</span>
                  </div>
                ))}
                {Object.keys(sum.by_location || {}).length > 0 && (
                  <SpendingByLocation tripId={tripId} byLocation={sum.by_location} home={home} />
                )}
              </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="expense-filters">
          <div className="relative flex-1 min-w-[170px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchant, place, amount…" data-testid="expense-search-input"
              className="w-full border border-zinc-300 pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <select value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="expense-filter-category" className="border border-zinc-300 px-2 py-2 text-sm bg-white">
            <option value="">All categories</option>
            {EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={fPay} onChange={(e) => setFPay(e.target.value)} data-testid="expense-filter-payment" className="border border-zinc-300 px-2 py-2 text-sm bg-white">
            <option value="">All payments</option>
            {PAYMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {(q || fCat || fPay) && (
            <button onClick={() => { setQ(""); setFCat(""); setFPay(""); }} data-testid="expense-filter-clear"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-zinc-900"><X className="w-3.5 h-3.5" /> Clear</button>
          )}
        </div>
      )}

      {/* List */}
      <div className="border border-zinc-300 bg-white divide-y divide-zinc-100">
        {items.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">
            No expenses yet — add one manually or scan a receipt and let TravelerPulse read it for you.
          </p>
        )}
        {items.length > 0 && filtered.length === 0 && <p className="p-4 text-sm text-muted-foreground" data-testid="expense-no-results">No expenses match your search.</p>}
        {filtered.map((e) => {
          const c = catOf(e.category);
          const mine = e.user_id && e.user_id === auth.currentUser?.uid; // logged by the viewer
          return (
            <div key={e.id} className="p-3 flex flex-wrap items-center gap-2" data-testid={`expense-row-${e.id}`}>
              <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">{e.date || "—"}</span>
              <div className="flex-1 min-w-[160px]">
                <div className="text-sm font-700 text-zinc-900 truncate">{e.merchant || e.description || "Expense"}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {[e.subcategory, e.location, e.payment_method && `${e.payment_method}${e.card_last4 ? ` •••• ${e.card_last4}` : ""}`, e.paid_by !== "Me" && !(mine && e.paid_by === e.added_by_name) && `paid by ${e.paid_by}`].filter(Boolean).join(" · ")}
                </div>
                {(e.address || e.phone || e.website || (e.added_by_name && !mine)) && (
                  <div className="text-[11px] text-muted-foreground truncate flex flex-wrap items-center gap-x-2">
                    {e.address && <span>{e.address}</span>}
                    {e.phone && <a href={`tel:${e.phone}`} className="text-[#b45309] hover:underline">{e.phone}</a>}
                    {e.website && <a href={e.website.startsWith("http") ? e.website : `https://${e.website}`} target="_blank" rel="noreferrer" className="text-[#b45309] hover:underline">website</a>}
                    {e.added_by_name && !mine && <span className="bg-amber-50 text-amber-800 px-1.5 py-px rounded-full" data-testid={`expense-addedby-${e.id}`}>added by {e.added_by_name}</span>}
                  </div>
                )}
              </div>
              <span className={`text-xs font-700 border px-1.5 py-0.5 shrink-0 ${c.cls}`}>{c.label}</span>
              {e.business && <span className="text-[10px] font-900 tracking-widest bg-zinc-800 text-white px-1.5 py-0.5 shrink-0">BIZ</span>}
              {e.reimbursable && <span className="text-[10px] font-700 border border-emerald-400 text-emerald-700 px-1.5 py-0.5 shrink-0">{REIMB.find(([k]) => k === e.reimbursement_status)?.[1] || "Reimbursable"}</span>}
              {e.receipt_url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${e.receipt_url}?auth=${token}`} target="_blank" rel="noreferrer"
                  title="View receipt" data-testid={`expense-receipt-${e.id}`} className="text-[#b45309] hover:text-amber-600 shrink-0"><Paperclip className="w-4 h-4" /></a>
              )}
              <div className="text-right shrink-0 w-28">
                <div className="font-mono font-700 text-sm" data-testid={`expense-amount-${e.id}`}>{money(e.amount, e.currency)}</div>
                {e.currency !== home && e.home_amount != null && <div className="font-mono text-xs text-muted-foreground">≈ {money(e.home_amount, home)}</div>}
              </div>
              {!readOnly && (
                <span className="flex gap-1 shrink-0">
                  <button onClick={() => duplicate(e)} title="Duplicate to the next day (recurring expenses like parking or breakfast)"
                    data-testid={`expense-duplicate-${e.id}`} className="p-1 text-zinc-300 hover:text-[#b45309]"><CopyPlus className="w-4 h-4" /></button>
                  <button onClick={() => setModal({ editId: e.id, form: { ...emptyForm(home), ...Object.fromEntries(Object.entries(e).map(([k, v]) => [k, v == null ? "" : v])) } })}
                    data-testid={`expense-edit-${e.id}`} className="p-1 text-zinc-300 hover:text-primary"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(e.id)} data-testid={`expense-delete-${e.id}`} className="p-1 text-zinc-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </span>
              )}
            </div>
          );
        })}
      </div>

      {modal && <ExpenseModal modal={modal} home={home} tripId={tripId} people={people} onSave={save} onClose={() => setModal(null)} />}
      {budgetOpen && <BudgetModal tripId={tripId} budget={data?.budget} onClose={() => setBudgetOpen(false)} onSaved={() => { setBudgetOpen(false); load(); }} />}
      <ReceiptGallery items={items} token={token} />
      <SpendingInsights tripId={tripId} />

      {settleOpen && <SettleUpModal split={sum?.split} home={home} people={people} tripId={tripId} tripTitle={tripTitle} onClose={() => setSettleOpen(false)} />}

      {items.length > 0 && <AskMyTrip tripId={tripId} />}
    </section>
  );
}

function SettleUpModal({ split, home, people = [], tripId, tripTitle, onClose }) {
  const [group, setGroup] = useState(null); // family-wide settlement (all members, not just "you")
  useEffect(() => {
    api.get(`/expenses/trip/${tripId}/settlement`).then((r) => setGroup(r.data)).catch(() => setGroup(null));
  }, [tripId]);
  const phoneOf = (name) => people.find((p) => p.name?.trim().toLowerCase() === name.trim().toLowerCase())?.phone;
  const entries = Object.entries(split?.per_person || {});
  const waLink = (name, amount) => {
    const phone = phoneOf(name);
    if (!phone) return null;
    const msg = `Hi ${name}! Friendly reminder from our trip${tripTitle ? ` "${tripTitle}"` : ""}: your share of the group expenses is ${money(amount, home)}. Tracked with TravelerPulse.pro — thanks!`;
    return `https://wa.me/${phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(msg)}`;
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="settle-modal">
      <div className="bg-white w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-700 text-lg flex items-center gap-2"><Users className="w-5 h-5 text-[#b45309]" /> Settle Up</h3>
          <button onClick={onClose} data-testid="settle-modal-close"><X className="w-5 h-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["You paid", split?.you_paid], ["Your share", split?.your_share], ["Owed to you", split?.others_owe_you]].map(([l, v]) => (
            <div key={l} className="border border-zinc-200 bg-zinc-50 p-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{l}</div>
              <div className="font-mono font-700 text-sm mt-0.5">{money(v, home)}</div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {entries.length === 0 && <p className="text-sm text-muted-foreground">Everyone is settled — no outstanding balances.</p>}
          {entries.map(([name, v]) => {
            const wa = v > 0 ? waLink(name, v) : null;
            const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
            return (
              <div key={name} className="border border-zinc-300 p-3 flex items-center gap-3" data-testid={`settle-row-${slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-700 text-sm">{name}</div>
                  <div className={`text-xs font-mono ${v > 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {v > 0 ? `owes you ${money(v, home)}` : `you owe ${money(-v, home)}`}
                  </div>
                </div>
                {v > 0 && (wa ? (
                  <a href={wa} target="_blank" rel="noopener noreferrer" data-testid={`settle-wa-${slug}`}
                    className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-3 py-1.5 text-xs font-700 hover:bg-emerald-600">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp reminder
                  </a>
                ) : (
                  <span className="text-[10px] text-muted-foreground text-right max-w-[130px]">Add a WhatsApp number to this traveler to send reminders</span>
                ))}
              </div>
            );
          })}
        </div>
        {/* Family settlement — all members, not just you */}
        {group && (group.transfers?.length > 0 || Object.keys(group.balances || {}).length > 0) && (
          <div className="border-t border-zinc-200 pt-3" data-testid="settle-group">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Family settlement — who owes whom</div>
              <a data-testid="settle-group-share"
                href={`https://wa.me/?text=${encodeURIComponent(
                  `*Trip expense settlement — ${tripTitle || "our trip"}*\n\n` +
                  `Balances (${group.home_currency}):\n` +
                  Object.entries(group.balances || {}).map(([n, v]) => `• ${n}: ${v > 0 ? "+" : ""}${v.toFixed(2)}`).join("\n") +
                  `\n\nWho pays whom:\n` +
                  (group.transfers || []).map((tr) => `• ${tr.from} → ${tr.to}: ${tr.amount.toFixed(2)} ${group.home_currency}`).join("\n") +
                  `\n\nvia TravelerPulse.pro`)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-[#25D366] text-white px-2.5 py-1.5 text-xs font-700 hover:bg-emerald-600">
                <MessageCircle className="w-3.5 h-3.5" /> Share to WhatsApp
              </a>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {Object.entries(group.balances || {}).map(([name, v]) => (
                <span key={name} className={`text-xs font-600 px-2 py-1 border ${v > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-800"}`}>
                  {name}: {v > 0 ? "+" : ""}{money(v, group.home_currency)}
                </span>
              ))}
            </div>
            {(group.transfers || []).map((tr, i) => (
              <div key={i} className="flex items-center gap-2 py-1 text-sm" data-testid={`settle-transfer-${i}`}>
                <span className="font-700">{tr.from}</span>
                <span className="text-muted-foreground text-xs">pays</span>
                <span className="font-mono font-700 text-[#b45309]">{money(tr.amount, group.home_currency)}</span>
                <span className="text-muted-foreground text-xs">to</span>
                <span className="font-700">{tr.to}</span>
              </div>
            ))}
            {group.unconverted > 0 && <p className="text-[10px] text-amber-700 mt-1">{group.unconverted} expense(s) skipped — no exchange rate to {group.home_currency}.</p>}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground">Balances are in {home}, converted from each expense's currency. Reminders open WhatsApp with a pre-written message — nothing is sent automatically.</p>
      </div>
    </div>
  );
}

function SpendingByLocation({ tripId, byLocation, home }) {
  const [showMap, setShowMap] = useState(false);
  const [points, setPoints] = useState(null);
  const [MapView, setMapView] = useState(null);
  const top = Object.entries(byLocation).slice(0, 8);
  const max = top.length ? top[0][1] : 1;
  const openMap = async () => {
    setShowMap(true);
    try {
      const [mod, r] = await Promise.all([import("../MapView"), api.get(`/expenses/trip/${tripId}/map`)]);
      setMapView(() => mod.default);
      setPoints(r.data.items.filter((p) => p.lat != null));
    } catch { toast.error("Could not load the spending map"); }
  };
  return (
    <div className="mt-4" data-testid="expense-locations">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Spending by location</div>
        {!showMap && <button onClick={openMap} data-testid="expense-map-btn" className="inline-flex items-center gap-1 text-xs font-700 text-[#b45309] hover:underline"><MapPin className="w-3.5 h-3.5" /> Show on map</button>}
      </div>
      {top.map(([loc, amt]) => (
        <div key={loc} className="flex items-center gap-2 py-1 text-sm" data-testid={`expense-loc-${loc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <span className="w-36 truncate text-xs">{loc}</span>
          <span className="flex-1 h-1.5 bg-zinc-100"><span className="block h-1.5 bg-emerald-600/60" style={{ width: `${Math.round(amt / max * 100)}%` }} /></span>
          <span className="font-mono text-xs">{money(amt, home)}</span>
        </div>
      ))}
      {showMap && (
        <div className="mt-3" data-testid="expense-map">
          {!MapView || points === null ? (
            <div className="h-40 flex items-center justify-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading map…</div>
          ) : points.length === 0 ? (
            <p className="text-xs text-muted-foreground">Couldn't place these locations on the map — use "City, Country" format on expenses.</p>
          ) : (
            <MapView height="280px" zoom={2} center={[points[0].lat, points[0].lon]}
              markers={points.map((p) => ({ lat: p.lat, lon: p.lon, radius: Math.max(5, Math.min(14, Math.sqrt(p.total))), popup: `${p.location}: ${money(p.total, home)}` }))} />
          )}
        </div>
      )}
    </div>
  );
}

function AskMyTrip({ tripId }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState(null);
  const ask = async (e) => {
    e?.preventDefault();
    if (!q.trim() || busy) return;
    setBusy(true);
    setAnswer(null);
    try {
      const r = await api.post(`/expenses/trip/${tripId}/ask`, { question: q.trim() });
      setAnswer(r.data.answer);
    } catch (err) { toast.error(err?.response?.data?.detail || "Assistant unavailable — try again shortly"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#b45309]/40 bg-amber-50/40 p-4 mt-4" data-testid="ask-my-trip">
      <div className="flex items-center gap-2 font-heading font-700 text-sm mb-2">
        <Sparkles className="w-4 h-4 text-[#b45309]" /> Ask My Trip
        <span className="font-400 text-xs text-muted-foreground">— e.g. "How much did I spend on food?" · "What was my most expensive day?"</span>
      </div>
      <form onSubmit={ask} className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="ask-trip-input"
          placeholder="Ask a question about this trip's expenses…"
          className="flex-1 border border-zinc-300 px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-[#b45309]" />
        <button type="submit" disabled={busy || !q.trim()} data-testid="ask-trip-submit"
          className="inline-flex items-center gap-1.5 bg-[#b45309] text-white px-4 py-2 text-sm font-700 hover:bg-amber-700 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ask
        </button>
      </form>
      {answer && <p className="text-sm text-zinc-800 mt-3 whitespace-pre-line border-l-2 border-[#b45309] pl-3" data-testid="ask-trip-answer">{answer}</p>}
    </div>
  );
}

function ExpenseModal({ modal, home, tripId, people = [], onSave, onClose }) {
  const [f, setF] = useState(modal.form);
  const [rateBusy, setRateBusy] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const receiptRef = useRef(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));
  const subs = catOf(f.category).subs;
  const peopleList = [...new Set(["Me", ...people])].filter(Boolean);

  const attachReceipt = async (file) => {
    if (!file) return;
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", "trip");
      fd.append("owner_id", tripId);
      fd.append("category", "receipt");
      fd.append("label", f.merchant ? `Receipt — ${f.merchant}` : "Receipt");
      const r = await api.post("/trips/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, receipt_id: r.data.id, receipt_url: r.data.url }));
      toast.success("Receipt attached");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not attach the receipt"); }
    finally { setAttaching(false); if (receiptRef.current) receiptRef.current.value = ""; }
  };

  // auto-fetch ECB rate when foreign currency & no rate yet
  useEffect(() => {
    const ccy = (f.currency || "").toUpperCase();
    if (!ccy || ccy === (f.home_currency || home) || !f.amount) return;
    if (f.exchange_rate) { // recompute home amount from existing rate
      setF((p) => ({ ...p, home_amount: p.amount && p.exchange_rate ? (p.amount * p.exchange_rate).toFixed(2) : p.home_amount }));
      return;
    }
    setRateBusy(true);
    api.get("/expenses/rates", { params: { base: ccy, to: f.home_currency || home } })
      .then((r) => setF((p) => ({ ...p, exchange_rate: r.data.rate, rate_source: "system",
        home_amount: p.amount ? (p.amount * r.data.rate).toFixed(2) : "" })))
      .catch(() => toast.info("Rate lookup unavailable — enter the exchange rate manually"))
      .finally(() => setRateBusy(false));
  }, [f.currency, f.amount]); // eslint-disable-line react-hooks/exhaustive-deps

  const foreign = (f.currency || "").toUpperCase() !== (f.home_currency || home);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="expense-modal">
      <div className="bg-white w-full max-w-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-700 text-lg flex items-center gap-2">
            {modal.fromScan ? <><ScanLine className="w-5 h-5 text-[#b45309]" /> Review Scanned Receipt</> : modal.editId ? "Edit Expense" : "Add Expense"}
          </h3>
          <button onClick={onClose} data-testid="expense-modal-close"><X className="w-5 h-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        {modal.fromScan && (
          <p className={`text-xs px-3 py-2 border ${modal.ocrError ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-cyan-50 border-cyan-200 text-cyan-900"}`} data-testid="expense-ocr-note">
            {modal.ocrError || "We read this from your receipt — please review and correct anything before saving. Receipts can be blurry or multilingual, so you always have the final say."}
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-2"><label className={lbl}>Merchant / Vendor</label>
            <input value={f.merchant} onChange={set("merchant")} data-testid="expense-form-merchant" className={input} placeholder="Marriott, Uber, Starbucks…" /></div>
          <div><label className={lbl}>Date</label><input type="date" value={f.date} onChange={set("date")} data-testid="expense-form-date" className={input} /></div>
          <div><label className={lbl}>Time</label><input type="time" value={f.time} onChange={set("time")} data-testid="expense-form-time" className={input} /></div>
          <div className="col-span-2 sm:col-span-4"><label className={lbl}>Description</label>
            <input value={f.description} onChange={set("description")} data-testid="expense-form-description" className={input} placeholder="What was purchased?" /></div>
          <div className="col-span-2"><label className={lbl}>Category</label>
            <select value={f.category} onChange={(e) => setF((p) => ({ ...p, category: e.target.value, subcategory: "" }))} data-testid="expense-form-category" className={input}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select></div>
          <div className="col-span-2"><label className={lbl}>Subcategory</label>
            <select value={subs.includes(f.subcategory) || !f.subcategory ? f.subcategory : "__custom"}
              onChange={(e) => setF((p) => ({ ...p, subcategory: e.target.value === "__custom" ? " " : e.target.value }))}
              data-testid="expense-form-subcategory" className={input}>
              <option value="">—</option>{subs.map((s) => <option key={s} value={s}>{s}</option>)}
              <option value="__custom">Other — specify…</option>
            </select>
            {f.subcategory && !subs.includes(f.subcategory) && (
              <input value={f.subcategory === " " ? "" : f.subcategory} onChange={set("subcategory")} autoFocus
                placeholder="Type your own subcategory…" data-testid="expense-form-subcategory-custom"
                className={`${input} mt-1.5`} />
            )}</div>
          <div><label className={lbl}>Amount *</label><input type="number" step="0.01" min="0" value={f.amount} onChange={set("amount")} data-testid="expense-form-amount" className={input} /></div>
          <div><label className={lbl}>Currency</label>
            <select value={f.currency} onChange={set("currency")} data-testid="expense-form-currency" className={input}>
              {[...new Set([f.currency, ...CURRENCIES])].filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div><label className={lbl}>Tax / VAT</label><input type="number" step="0.01" value={f.tax} onChange={set("tax")} data-testid="expense-form-tax" className={input} /></div>
          <div><label className={lbl}>Tip</label><input type="number" step="0.01" value={f.tip} onChange={set("tip")} data-testid="expense-form-tip" className={input} /></div>
          {foreign && (
            <>
              <div className="col-span-2"><label className={lbl}>Exchange rate {rateBusy && <Loader2 className="inline w-3 h-3 animate-spin" />} <span className="font-400 text-muted-foreground">(1 {f.currency} → {f.home_currency || home}; edit to match your card)</span></label>
                <input type="number" step="0.0001" value={f.exchange_rate} data-testid="expense-form-rate"
                  onChange={(e) => setF((p) => ({ ...p, exchange_rate: e.target.value, rate_source: "user",
                    home_amount: p.amount && e.target.value ? (p.amount * e.target.value).toFixed(2) : p.home_amount }))} className={input} /></div>
              <div className="col-span-2"><label className={lbl}>Amount in {f.home_currency || home}</label>
                <input type="number" step="0.01" value={f.home_amount} onChange={set("home_amount")} data-testid="expense-form-home-amount" className={input} /></div>
            </>
          )}
          <div><label className={lbl}>Payment</label>
            <select value={f.payment_method || ""} onChange={set("payment_method")} data-testid="expense-form-payment" className={input}>
              <option value="">—</option>{PAYMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select></div>
          <div><label className={lbl}>Card last 4</label><input maxLength={4} value={f.card_last4 || ""} onChange={set("card_last4")} data-testid="expense-form-last4" className={input} placeholder="4821" /></div>
          <div><label className={lbl}>Paid by</label><input value={f.paid_by} onChange={set("paid_by")} list="trip-people" data-testid="expense-form-paidby" className={input} placeholder="Me / Tom / Company" /></div>
          <div><label className={lbl}>Expense for</label><input value={f.expense_for} onChange={set("expense_for")} list="trip-people" data-testid="expense-form-for" className={input} placeholder="Me / Everyone" /></div>
          <datalist id="trip-people">{peopleList.map((p) => <option key={p} value={p} />)}</datalist>
          <div className="col-span-2"><label className={lbl}>Location</label>
            <input value={f.location || ""} onChange={set("location")} data-testid="expense-form-location" className={input} placeholder="Frankfurt, Germany" /></div>
          <div className="col-span-2"><label className={lbl}>Store address</label>
            <input value={f.address || ""} onChange={set("address")} data-testid="expense-form-address" className={input} placeholder="12 Marine Drive, Fort Kochi" /></div>
          <div><label className={lbl}>Store phone</label>
            <input value={f.phone || ""} onChange={set("phone")} data-testid="expense-form-phone" className={input} placeholder="+91 484 000 0000" /></div>
          <div><label className={lbl}>Store email</label>
            <input value={f.email || ""} onChange={set("email")} data-testid="expense-form-email" className={input} placeholder="hello@store.com" /></div>
          <div className="col-span-2"><label className={lbl}>Website</label>
            <input value={f.website || ""} onChange={set("website")} data-testid="expense-form-website" className={input} placeholder="https://…" /></div>
          <div className="col-span-2 flex items-end gap-4 pb-1">
            <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!f.business} onChange={set("business")} data-testid="expense-form-business" className="accent-[#b45309]" /> Business</label>
            <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={!!f.reimbursable} onChange={set("reimbursable")} data-testid="expense-form-reimbursable" className="accent-[#b45309]" /> Reimbursable</label>
            {f.reimbursable && (
              <select value={f.reimbursement_status} onChange={set("reimbursement_status")} data-testid="expense-form-reimb-status" className="border border-zinc-300 px-2 py-1.5 text-xs bg-white">
                {REIMB.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            )}
          </div>
          <div className="col-span-2 sm:col-span-4" data-testid="expense-split-editor">
            <div className="flex items-center justify-between mb-1">
              <label className={lbl}>Split with co-travelers <span className="font-400 text-muted-foreground">(amounts in {f.currency}; use "Me" for your own share)</span></label>
              <div className="flex items-center gap-3">
                {peopleList.length > 1 && (
                  <button type="button" data-testid="expense-split-travelers"
                    onClick={() => setF((p) => ({ ...p, split: peopleList.map((n) => ({ name: n, amount: p.amount ? (p.amount / peopleList.length).toFixed(2) : "" })) }))}
                    className="text-xs font-700 text-[#b45309] hover:underline">Split between all {peopleList.length} travelers</button>
                )}
                <button type="button" data-testid="expense-split-add"
                  onClick={() => setF((p) => ({ ...p, split: [...(p.split || []), { name: "", amount: "" }] }))}
                  className="text-xs font-700 text-[#b45309] hover:underline">+ Add person</button>
              </div>
            </div>
            {(f.split || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5">
                <input value={s.name} placeholder="Name (Me, Jane…)" data-testid={`expense-split-name-${i}`}
                  onChange={(e) => setF((p) => ({ ...p, split: p.split.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))}
                  className="flex-1 border border-zinc-300 px-2.5 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
                <input type="number" step="0.01" value={s.amount} placeholder="Share" data-testid={`expense-split-amount-${i}`}
                  onChange={(e) => setF((p) => ({ ...p, split: p.split.map((x, j) => j === i ? { ...x, amount: e.target.value } : x) }))}
                  className="w-28 border border-zinc-300 px-2.5 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
                <button type="button" onClick={() => setF((p) => ({ ...p, split: p.split.filter((_, j) => j !== i) }))}
                  className="text-zinc-300 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {(f.split || []).length > 1 && f.amount && (
              <button type="button" data-testid="expense-split-even"
                onClick={() => setF((p) => ({ ...p, split: p.split.map((x) => ({ ...x, amount: (p.amount / p.split.length).toFixed(2) })) }))}
                className="text-xs text-muted-foreground hover:text-[#b45309]">Split {f.amount} {f.currency} evenly between {(f.split || []).length} people</button>
            )}
          </div>
          <div className="col-span-2 sm:col-span-4"><label className={lbl}>Notes</label>
            <textarea rows={2} value={f.notes || ""} onChange={set("notes")} data-testid="expense-form-notes" className={`${input} resize-y`} /></div>
        </div>
        {f.receipt_url ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Receipt attached — it stays linked to this expense.</p>
        ) : !modal.fromScan && (
          <div>
            <button type="button" onClick={() => receiptRef.current?.click()} disabled={attaching} data-testid="expense-attach-receipt"
              className="inline-flex items-center gap-1.5 text-xs font-700 text-[#b45309] hover:underline disabled:opacity-50">
              {attaching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {attaching ? "Uploading…" : "Attach receipt photo / PDF"}
            </button>
            <input ref={receiptRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
              data-testid="expense-attach-input" onChange={(e) => attachReceipt(e.target.files?.[0])} />
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 hover:bg-zinc-50">Cancel</button>
          <button onClick={() => onSave(f, modal.editId)} data-testid="expense-form-save"
            className="px-5 py-2 text-sm font-700 bg-[#b45309] text-white hover:bg-amber-700">
            {modal.fromScan ? "Confirm & Save" : "Save Expense"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BudgetModal({ tripId, budget, onClose, onSaved }) {
  const [total, setTotal] = useState(budget?.total_budget ?? "");
  const [ccy, setCcy] = useState(budget?.home_currency || "USD");
  const [cats, setCats] = useState(budget?.category_budgets || {});
  const save = async () => {
    try {
      await api.put(`/expenses/trip/${tripId}/budget`, { total_budget: total || null, home_currency: ccy, category_budgets: cats });
      toast.success("Budget saved");
      onSaved();
    } catch { toast.error("Could not save budget"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4" data-testid="budget-modal">
      <div className="bg-white w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-700 text-lg flex items-center gap-2"><PiggyBank className="w-5 h-5 text-[#b45309]" /> Trip Budget</h3>
          <button onClick={onClose} data-testid="budget-modal-close"><X className="w-5 h-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Total budget</label><input type="number" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="budget-total-input" className={input} placeholder="5000" /></div>
          <div><label className={lbl}>Reporting currency</label>
            <select value={ccy} onChange={(e) => setCcy(e.target.value)} data-testid="budget-currency-select" className={input}>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></div>
        </div>
        <div>
          <div className={lbl}>Category budgets (optional)</div>
          <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
            {EXPENSE_CATEGORIES.map((c) => (
              <div key={c.id} className="flex items-center gap-2">
                <span className={`text-xs font-700 border px-1.5 py-0.5 w-40 truncate ${c.cls}`}>{c.label}</span>
                <input type="number" step="0.01" value={cats[c.id] ?? ""} data-testid={`budget-cat-${c.id}`}
                  onChange={(e) => setCats((p) => ({ ...p, [c.id]: e.target.value }))}
                  className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-zinc-300 hover:bg-zinc-50">Cancel</button>
          <button onClick={save} data-testid="budget-save" className="px-5 py-2 text-sm font-700 bg-[#b45309] text-white hover:bg-amber-700">Save Budget</button>
        </div>
      </div>
    </div>
  );
}

/* Owner-only: public shareable expense summary — link + iframe embed for other sites. */
function ShareSummary({ tripId, tripTitle }) {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null); // {enabled, token, path}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/expenses/trip/${tripId}/share`).then((r) => setShare(r.data)).catch(() => setShare({ enabled: false }));
  }, [open, tripId]);

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const r = await api.post(`/expenses/trip/${tripId}/share`, { enabled });
      setShare(r.data);
      toast.success(enabled ? "Expense summary link is live" : "Sharing turned off");
    } catch { toast.error("Could not update sharing"); }
    setBusy(false);
  };

  const url = share?.enabled ? `${window.location.origin}${share.path}` : "";
  const iframe = share?.enabled
    ? `<iframe src="${url}?embed=1" width="100%" height="640" style="border:1px solid #d4d4d8" title="Trip expense summary"></iframe>`
    : "";
  const copy = (text, label) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="expense-share-btn"
        className="inline-flex items-center gap-1.5 border border-emerald-600 text-emerald-700 px-3 py-2 text-sm font-700 hover:bg-emerald-50">
        <Share2 className="w-4 h-4" /> Share summary
      </button>
      {open && (
        <div className="w-full border border-emerald-300 bg-emerald-50/60 p-4 text-sm" data-testid="expense-share-panel">
          <div className="font-700 mb-1">Public expense summary</div>
          <p className="text-xs text-muted-foreground mb-3">
            A read-only page with totals, categories and the settlement — post the link anywhere or embed it
            on another site. Receipts, notes and documents are never included. Turn it off anytime.
          </p>
          {!share ? <span className="text-xs text-muted-foreground">Loading…</span> : share.enabled ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid="expense-share-insights">
                <span className="font-800 text-emerald-800">
                  👁 Viewed {share.views || 0} time{(share.views || 0) === 1 ? "" : "s"}
                </span>
                {share.last_viewed_at && (
                  <span className="text-muted-foreground">last {new Date(share.last_viewed_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                )}
                {(share.view_log || []).length > 1 && (
                  <span className="text-muted-foreground" title={share.view_log.map((v) => new Date(v.at).toLocaleString()).join("\n")}>
                    · recent: {share.view_log.slice(0, 3).map((v) => new Date(v.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })).join(", ")}{share.view_log.length > 3 ? " …" : ""}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <code className="flex-1 min-w-[220px] bg-white border border-zinc-300 px-2 py-1.5 text-xs truncate" data-testid="expense-share-url">{url}</code>
                <button onClick={() => copy(url, "Link")} data-testid="expense-share-copy"
                  className="inline-flex items-center gap-1 border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-700 hover:border-emerald-600"><Copy className="w-3.5 h-3.5" /> Copy link</button>
                <a href={`https://wa.me/?text=${encodeURIComponent(`Expense summary for "${tripTitle}": ${url}`)}`} target="_blank" rel="noreferrer"
                  data-testid="expense-share-whatsapp"
                  className="inline-flex items-center gap-1 bg-[#25D366] text-white px-2.5 py-1.5 text-xs font-700 hover:opacity-90"><Send className="w-3.5 h-3.5" /> WhatsApp</a>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <code className="flex-1 min-w-[220px] bg-white border border-zinc-300 px-2 py-1.5 text-[10px] truncate" data-testid="expense-share-iframe">{iframe}</code>
                <button onClick={() => copy(iframe, "Embed code")} data-testid="expense-share-copy-iframe"
                  className="inline-flex items-center gap-1 border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-700 hover:border-emerald-600"><Code2 className="w-3.5 h-3.5" /> Copy embed code</button>
              </div>
              <button onClick={() => toggle(false)} disabled={busy} data-testid="expense-share-disable"
                className="text-xs font-700 text-rose-700 hover:underline disabled:opacity-50">Turn off sharing</button>
              <a href="/embed" target="_blank" rel="noreferrer" data-testid="expense-share-embed-gallery"
                className="ml-3 text-xs font-700 text-emerald-700 hover:underline">Embed gallery →</a>
            </div>
          ) : (
            <button onClick={() => toggle(true)} disabled={busy} data-testid="expense-share-enable"
              className="inline-flex items-center gap-1.5 bg-emerald-700 text-white px-4 py-2 text-xs font-700 hover:bg-emerald-600 disabled:opacity-50">
              <Share2 className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create share link"}
            </button>
          )}
        </div>
      )}
    </>
  );
}
